<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate, useParams } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Row } from '@sveltestrap/sveltestrap';
  import { formatDistance } from 'date-fns';

  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PerformanceInsights } from '@client/components/performance-insights';

  import { ActionsCell, StatusCell } from '@client/pages/marketing/campaigns/manage/cell-formatters';
  import { EditForm } from '@client/pages/marketing/campaigns/manage/edit-form';
  import { RowExpansion } from '@client/pages/marketing/performance/row-expansion';

  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';
  import { TargetingService } from '@client/services/targeting';

  import { appStore } from '@client/stores/app';
  import { defaultCampaignTags } from '@client/stores/domain-settings';

  import { determineCampaignStatus } from '@client/utils/campaigns';
  import { structureTree } from '@client/utils/folder-tree';
  import { sanitizeTagValue } from '@client/utils/sanitize';

  import { AD_TYPES, CAMPAIGN_CATEGORIES, CAMPAIGN_CATEGORIES_ARRAY, LINK_TYPES } from '@client/enums';

  import { deepCopyArray } from '@common/utils/arrays';
  import { formatDate, isValidDate } from '@common/utils/dates';
  import { deepCopy } from '@common/utils/objects';
  import { isValidUrl } from '@common/utils/validation';

  const { basepath } = getContext('config');

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const params = useParams();
  const navigate = useNavigate();

  let loaded = false;
  let updating = false;

  let campaign = null;

  let originalCampaign = {};

  let campaignName = $params.id;

  let selectedItems = [];

  let selectedItemField = 'campaigns';

  let categories = deepCopyArray(CAMPAIGN_CATEGORIES_ARRAY);

  let tags = [];
  let selectedCategories = [];
  let selectedTags = [];
  let filterText = '';
  let timeRemaining = '';

  let startDate = '';
  let endDate = '';
  let dateFormat = 'EEE, MMM d, yy @ h:mm a';

  let isOpen = false;

  let defaultYear = new Date().getFullYear();
  let defaultMonth = new Date().getMonth();

  let days = 30;

  let defaultStartDate = new Date();

  defaultStartDate.setDate(defaultStartDate.getDate() + 1 - 366);

  let defaultEndDate = new Date();

  defaultEndDate.setDate(defaultEndDate.getDate() + 1);

  let chartRangeDays = null;

  let formattedStartDate = '';
  let formattedEndDate = '';

  let validationError = false;
  let errorMessage = '';

  let documentItems = [];
  let selectedDocument = '';

  let videoItems = [];
  let selectedVideo = '';

  let fileData = {};
  let files = [];

  let activeTargeting = {};

  let accounts = [];
  let recipients = [];
  let domains = [];

  let folders = [];
  let selectedFolders = [];
  let selectedLabels = [];

  let originalRecipients = [];
  let originalDomains = [];

  const onUpdate = async () => {
    $appStore.loading = true;

    if (campaign.linktype === LINK_TYPES.url.id) {
      campaign.videoinfo = null;
      campaign.videoname = null;
      campaign.docinfo = null;
      campaign.docname = null;
    }

    if (campaign.linktype === LINK_TYPES.doc.id) {
      campaign.url = null;
      campaign.videoinfo = null;
      campaign.videoname = null;
    }

    if (campaign.linktype === LINK_TYPES.video.id) {
      campaign.url = null;
      campaign.docinfo = null;
      campaign.docname = null;
    }

    if (campaign.adtype === AD_TYPES.image.id) {
      campaign.textstring = null;
      campaign.textcss = null;
    }

    if (campaign.adtype === AD_TYPES.text.id) {
      campaign.filename = null;
      campaign.filesize = null;
      campaign.hash = null;
      campaign.metadata = null;
      campaign.mimetype = null;
      campaign.alttext = null;
      campaign.inline = null;
      campaign.is2x = null;
      campaign.isplaceholder = null;
      campaign.previewURL = '';
    }

    if (startDate && endDate) {
      endDate = new Date(endDate).toISOString();
      startDate = new Date(startDate).toISOString();
    }

    if (campaign.enableschedule) {
      campaign.schedule = {
        startDate,
        endDate
      };
    }

    if (!campaign.enableschedule && !dateInputValue) {
      campaign.schedule = null;
      startDate = '';
      endDate = '';
    }

    activeTargeting = await TargetingService.get();

    campaign = determineCampaignStatus(campaign, activeTargeting);

    const findTargetingDiff = (originalArray, modifiedArray) => {
      const modifiedSet = new Set(modifiedArray);
      return originalArray.filter((element) => !modifiedSet.has(element));
    };

    const campaignTargeting = [...recipients, ...domains];

    const currentAccountTargeting = activeTargeting.accounts
      .filter(({ ads }) => ads.filter(({ text }) => text === campaignName).length)
      .map(({ name }) => name);

    const removedAccounts = findTargetingDiff(currentAccountTargeting, campaignTargeting);

    const updatedAccounts = activeTargeting.accounts.map((account) => {
      if (removedAccounts.length) {
        if (removedAccounts.includes(account.name)) {
          account.ads = account.ads.filter(({ text }) => text !== campaign.rulename);
        }
      }

      return account;
    });

    campaignTargeting.forEach((name) => {
      const existingAccount = updatedAccounts.find((account) => account.name === name);

      if (existingAccount) {
        const existingAd = existingAccount.ads.find(({ text }) => text === campaign.rulename);

        if (!existingAd) {
          existingAccount.ads.push({
            text: campaign.rulename
          });
        }
      } else {
        updatedAccounts.push({
          name,
          ads: [
            {
              text: campaign.rulename
            }
          ],
          tags: [],
          isNew: true
        });
      }
    });

    const combinedTargeting = {
      accounts: updatedAccounts,
      departments: activeTargeting.departments
    };

    console.log('Combined Targeting:', combinedTargeting);

    const targetingResponse = await TargetingService.update(combinedTargeting);

    if (targetingResponse?.status >= 400) {
      notify(
        $format('label.FAILURE.CREATE', {
          values: { type: campaignData.rulename }
        }),
        'error'
      );

      targetingError = true;
      targetingErrorMessage = targetingResponse?.message;

      return;
    }

    const response = await CampaignsService.updateCampaign(campaign);

    await setupTargeting();

    $appStore.loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.UPDATE', { values: { rulename: campaign.rulename } }), 'error');
      return;
    }

    notify($format('label.SUCCESS.UPDATE', { values: { item: response.rulename } }));

    if (response.rulename !== originalCampaign.rulename) {
      navigate(`/marketing/campaigns/${response.rulename}`);
      campaignName = response.rulename;
    }

    originalCampaign = deepCopy(campaign);
  };

  const update = async (campaignObject) => {
    campaign = { ...determineCampaignStatus(campaignObject, activeTargeting) };

    if (campaign?.schedule?.startDate) {
      startDate = new Date(campaign.schedule.startDate);
      endDate = new Date(campaign.schedule.endDate);
    }
  };

  const calculateTimeRemaining = () => {
    const dateStart = new Date(startDate);
    const dateEnd = new Date(endDate);
    const start = dateStart.getTime();
    const end = dateEnd.getTime();
    const now = new Date().getTime();

    if (start <= now && end >= now) {
      return `(${$format('label.CAMPAIGN_STARTED', { values: { time: formatDistance(now, end) } })})`;
    }

    if (start > now) {
      return `(${$format('label.CAMPAIGN_UPCOMING')})`;
    }

    return '';
  };

  const onDatePickerChange = (dateRange = {}) => {
    if (dateRange.startDate && dateRange.endDate) {
      const dateStart = new Date(dateRange.startDate);
      const dateEnd = new Date(dateRange.endDate);

      if (isValidDate(dateRange.startDate) && isValidDate(dateRange.endDate)) {
        if (dateStart !== startDate) {
          startDate = dateStart;
        }

        if (dateEnd !== endDate) {
          endDate = dateEnd;
        }

        campaign.schedule = {
          startDate,
          endDate
        };
      }

      timeRemaining = calculateTimeRemaining();
    }
  };

  const onDateInputChange = () => {
    const splitDates = dateInputValue.split(' - ');
    const [start, end] = splitDates;

    if (start && end) {
      const dateStart = new Date(start);
      const dateEnd = new Date(end);

      if (isValidDate(dateStart) && isValidDate(dateEnd)) {
        if (dateStart !== startDate) {
          startDate = dateStart;
        }

        if (dateEnd !== endDate) {
          endDate = dateEnd;
        }

        campaign.schedule = {
          startDate,
          endDate
        };
      }
    } else {
      campaign.schedule = null;
      startDate = '';
      endDate = '';
    }

    timeRemaining = calculateTimeRemaining();
  };

  const toggleDatePicker = () => {
    return startDate && !endDate ? (isOpen = true) : (isOpen = !isOpen);
  };

  const formatDateString = (dateString) => {
    if (!dateString) {
      return '';
    }

    return formatDate(new Date(dateString), dateFormat);
  };

  const onTagChange = (e) => {
    const currentTags = tags || [];

    filterText = sanitizeTagValue(filterText);

    if (!selectedTags) {
      selectedTags = [];
      campaign.tags = [];
      return;
    }

    if (e.key === 'Enter' || e.type === 'blur') {
      if (selectedTags.map(({ value }) => value).includes(filterText)) {
        filterText = '';
        return;
      }

      if (!currentTags.includes(filterText) && filterText.length) {
        selectedTags = [{ label: filterText, value: filterText }, ...selectedTags];
        filterText = '';
      }
    } else {
      if (!filterText.length && e.detail.value) {
        const selected = e.detail.value;

        if (!currentTags.includes(selected) && selected.length) {
          selectedTags = [selected, ...selectedTags];
        }
      }
    }

    let tagsObject = selectedTags.map((tag) => ({ text: tag.value }));

    campaign.tags = tagsObject;
  };

  const onTagClear = (e) => {
    if (!selectedTags) {
      selectedTags = [];
      campaign.tags = [];

      return;
    }

    selectedTags = selectedTags.filter(({ value }) => value !== e.detail.value);

    campaign.tags = selectedTags.map(({ value }) => ({ text: value }));
  };

  const onCategoryChange = () => {
    let categoriesObject = {};

    if (!selectedCategories) {
      selectedCategories = [];
      campaign.category = {};
      return;
    }

    selectedCategories = selectedCategories.map((category) => {
      categoriesObject[category.value] = true;
      return { ...category, label: $format(category.label), checked: true };
    });

    campaign.category = categoriesObject;
  };

  const setDefaultFolderSelection = (id, fldrs) => {
    for (const folder of fldrs) {
      if (folder.id === id) {
        onFolderSelection(folder);
      }

      if (folder.files?.length) {
        setDefaultFolderSelection(id, folder.files);
      }
    }
  };

  const setupAssetFolderTree = async () => {
    const response = await AssetsService.getAssetFolders();

    if (!response.length) {
      folders = [];
      return;
    }

    folders = [structureTree(response)];

    if (campaign?.folderid) {
      setDefaultFolderSelection(campaign?.folderid, folders);
    }
  };

  const resetFolderSelections = () => {
    selectedFolders = [];
    selectedLabels = [];
  };

  const updateFolderSelections = (id, name, override) => {
    const exists = override ?? selectedFolders.includes(id);

    if (exists) {
      if (selectedFolders.includes(id)) {
        selectedFolders.splice(selectedFolders.indexOf(id), 1);
        selectedLabels.splice(selectedLabels.indexOf(name), 1);
      }
    } else {
      resetFolderSelections();

      if (!selectedFolders.includes(id)) {
        selectedFolders.push(id);
        selectedLabels.push(name);
      }
    }

    selectedFolders = [...selectedFolders];
    selectedLabels = [...selectedLabels];

    campaign.folderid = selectedFolders.length ? selectedFolders[0] : null;
  };

  const onFolderSelection = ({ id, name }, override) => {
    updateFolderSelections(id, name, override);
  };

  const onSelect = (e, file) => {
    e.preventDefault();
    e.stopPropagation();

    onFolderSelection(file);
  };

  const onClickAction = (type) => {
    campaign.params = [];
    campaign.linktype = type;

    if (type === LINK_TYPES.url.id) {
      onUrlChange();
    }
  };

  const onUrlChange = () => {
    if (!campaign.url?.length) {
      campaign.params = [];
    }

    if (campaign.url?.length && !validationError) {
      const urlObj = new URL(campaign?.url);
      const searchParams = new URLSearchParams(urlObj.search);

      campaign.params = [];

      if (searchParams.size > 0) {
        searchParams.forEach((value, name) => {
          const findIndex = campaign.params.findIndex((param) => param.name === name);
          if (findIndex === -1) {
            campaign.params.push({ name, value });
          } else {
            campaign.params[findIndex].value = value;
          }
        });

        campaign.params = [...campaign.params];
      }

      return campaign?.url;
    }
  };

  const onParamsChange = () => {
    const hasParams = campaign.params.length;
    const hasQueryParams = campaign.url.indexOf('?') > -1;

    let url = campaign.url.slice(
      0,
      hasQueryParams ? campaign.url.indexOf('?') + (hasParams ? 1 : 0) : campaign.url.length
    );

    if (hasParams) {
      const pairs = campaign.params.map(({ name, value }) => `${name}=${value}`);
      url += hasQueryParams ? pairs.join('&') : `?${pairs.join('&')}`;
    }

    campaign.url = url;
  };

  const onDocumentChange = () => {
    campaign.docinfo = {
      ...selectedDocument
    };

    campaign.docname = selectedDocument?.rulename;
  };

  const onVideoChange = () => {
    campaign.videoinfo = {
      ...selectedVideo
    };

    campaign.videoname = selectedVideo?.rulename;
  };

  const onAdType = (e) => {
    files = [];
    fileData = {};

    campaign.adtype = e.target.innerText.toLowerCase();
  };

  const onFileUpload = async () => {
    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: file.filename } }), 'error');

      return;
    }

    const { hash, filesize, filename, metadata } = response;

    fileData = {
      ...fileData,
      hash,
      filesize,
      filename,
      metadata
    };

    campaign = {
      ...campaign,
      filename: fileData?.filename || campaign?.filename,
      filesize: fileData?.filesize || campaign?.filesize,
      hash: fileData?.hash || campaign?.hash,
      metadata: fileData?.metadata || campaign?.metadata,
      mimetype: fileData?.metadata?.mimetype || campaign?.mimetype
    };
  };

  const onRemoveFile = () => {
    files = [];
    fileData = {};
  };

  const onValidation = () => {
    validationError = !isValidUrl(campaign.url);

    if (validationError) {
      errorMessage = $format('label.VALIDATION_ERROR_NO_VALID_URL_CAMPAIGNS');
    }
  };

  const setupTargeting = async () => {
    activeTargeting = await TargetingService.get();

    originalRecipients = [];
    originalDomains = [];
    accounts = [];
    recipients = [];
    domains = [];

    campaign = determineCampaignStatus(campaign, activeTargeting);

    accounts = activeTargeting.accounts
      .filter(({ ads }) => ads.filter(({ text }) => text === campaignName).length)
      .map(({ name }) => {
        if (name.indexOf('@') > -1) {
          recipients.push(name);
        } else {
          domains.push(name);
        }

        return name;
      });

    recipients = [...recipients];
    domains = [...domains];

    originalRecipients = deepCopyArray(recipients);
    originalDomains = deepCopyArray(domains);
  };

  const setupAssets = async () => {
    setupAssetFolderTree();

    const documentList = await CampaignsService.getDocumentList();
    const videoList = await CampaignsService.getVideoList();

    documentItems = documentList.map(({ rulename, ...rest }) => ({
      label: rulename,
      value: rulename,
      rulename,
      ...rest
    }));

    videoItems = videoList.map(({ rulename, ...rest }) => ({
      label: rulename,
      value: rulename,
      rulename,
      ...rest
    }));

    selectedDocument = '';
    selectedVideo = '';
    files = [];
    fileData = {};

    if (campaign?.linktype === LINK_TYPES.doc.id) {
      selectedDocument = documentItems.find((item) => item.value === campaign.docinfo.rulename);
    }

    if (campaign?.linktype === LINK_TYPES.video.id) {
      selectedVideo = videoItems.find((item) => item.value === campaign.videoinfo.rulename);
    }
  };

  const setupCategories = () => {
    selectedCategories = Object.keys(campaign.category || {}).map((key) => ({
      label: $format(CAMPAIGN_CATEGORIES[key].label) || key,
      value: key
    }));
  };

  const setupTags = () => {
    selectedTags = campaign.tags.map(({ text }) => text);

    const targertingTags = activeTargeting?.departments.map(({ tags }) => tags.map(({ text }) => text)).flat() || [];

    tags = Array.from(
      new Map(
        [...($defaultCampaignTags || []), ...(targertingTags || []), ...selectedTags].sort().map((text) => [
          text,
          {
            label: text,
            value: text,
            checked: selectedTags.includes(text)
          }
        ])
      ).values()
    );
  };

  const setupCampaign = async () => {
    await setupTargeting();
    await setupAssets();

    setupCategories();
    setupTags();

    if (campaign?.schedule?.startDate) {
      startDate = new Date(campaign.schedule.startDate);
      endDate = new Date(campaign.schedule.endDate);

      timeRemaining = calculateTimeRemaining();
    }

    campaign = { ...campaign };
    originalCampaign = deepCopy(campaign);
  };

  onMount(async () => {
    loaded = false;

    campaign = await CampaignsService.getById($params.id);

    if (!campaign) {
      navigate('/marketing/campaigns');
      return;
    }

    selectedItems = [
      {
        label: campaign.rulename,
        value: campaign.rulename,
        checked: false
      }
    ];

    await setupCampaign();

    loaded = true;
  });

  $: hasUrl = campaign?.url?.length;

  $: isDisabled =
    (JSON.stringify(originalCampaign) === JSON.stringify(campaign) &&
      JSON.stringify(originalRecipients) === JSON.stringify(recipients) &&
      JSON.stringify(originalDomains) === JSON.stringify(domains)) ||
    (campaign?.enableschedule && (!startDate || !endDate)) ||
    (campaign?.linktype === LINK_TYPES.url.id && !hasUrl) ||
    (campaign?.linktype === LINK_TYPES.url.id && validationError) ||
    (campaign?.linktype === LINK_TYPES.doc.id && !selectedDocument) ||
    (campaign?.linktype === LINK_TYPES.video.id && !selectedVideo) ||
    (campaign?.adtype === AD_TYPES.image.id && !(files.length || campaign?.metadata?.origfile)) ||
    (campaign?.adtype === AD_TYPES.text.id && !campaign.textstring);
  $: formattedStartDate = formatDateString(startDate);
  $: formattedEndDate = formatDateString(endDate);
  $: dateInputValue = `${formattedStartDate} - ${formattedEndDate}`;
</script>

<div id="campaign-editor" class="page campaign-editor">
  <Loader show={!loaded || updating} backdrop />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.MARKETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.CAMPAIGNS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.EDIT_CAMPAIGN')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center" style="max-width: 1080px;">
        <Col xs="8" sm="6" md="6">
          <h1 class="page-title">
            {campaignName}
          </h1>
        </Col>
        <Col xs="4" sm="6" md="6" class="text-right">
          {#if campaign?.rulename}
            <span class="hide--mobile">
              <ActionsCell bind:row={campaign} props={{ update }} isEditing />
            </span>
          {/if}

          <div class="action-divider hide--mobile"></div>

          <Button color="secondary" class="hide--mobile me-2" on:click={() => navigate('/marketing/campaigns')}>
            {$format('label.CANCEL')}
          </Button>

          <Button color="primary" bind:disabled={isDisabled} on:click={onUpdate}>
            {$format('label.UPDATE')}
          </Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    {#if campaign}
      <div class="metrics-rollup">
        <div class="performance-insights">
          <PerformanceInsights
            filtered={[campaign]}
            {selectedItems}
            {selectedItemField}
            startDate={(campaign?.enableschedule && campaign?.schedule?.startDate) || defaultStartDate}
            endDate={(campaign?.enableschedule && campaign?.schedule?.endDate) || defaultEndDate}
            bind:daysOverride={chartRangeDays}
            bind:updating
            isEditView={true}
          >
            <div class="campaign-details" slot="summary">
              <div class="status">
                <StatusCell bind:row={campaign} />
              </div>
              <div class="name">
                {$format('label.CAMPAIGN_NAME', { values: { name: campaignName } })}
              </div>
              <div class="schedule">
                {#if campaign.enableschedule}
                  {formattedStartDate} - {formattedEndDate}
                  {#if campaign.active}
                    <span class="hide--mobile">{timeRemaining}</span>
                    <div class="hide--desktop">{timeRemaining}</div>
                  {/if}
                {:else}
                  {$format('label.CAMPAIGN_NOT_SCHEDULED')}
                {/if}
              </div>
            </div>
          </PerformanceInsights>
        </div>
      </div>

      <div class="campaign-layout">
        <EditForm
          bind:campaign
          bind:startDate
          bind:endDate
          bind:isOpen
          bind:defaultMonth
          bind:defaultYear
          bind:dateInputValue
          bind:tags
          bind:selectedTags
          bind:filterText
          bind:categories
          bind:selectedCategories
          bind:folders
          bind:selectedFolders
          bind:selectedLabels
          bind:documentItems
          bind:selectedDocument
          bind:videoItems
          bind:selectedVideo
          bind:files
          bind:validationError
          bind:errorMessage
          bind:recipients
          bind:domains
          {toggleDatePicker}
          {onDateInputChange}
          {onDatePickerChange}
          {onTagChange}
          {onTagClear}
          {onCategoryChange}
          {onSelect}
          {onClickAction}
          {onDocumentChange}
          {onVideoChange}
          {onValidation}
          {onUrlChange}
          {onParamsChange}
          {onAdType}
          {onFileUpload}
          {onRemoveFile}
        />

        <h2>{$format('label.INSIGHTS')}</h2>
        <RowExpansion row={campaign} showRange basicView bind:days />
      </div>
    {/if}
  </div>
</div>

<style lang="scss" src="./edit.scss"></style>
